import { Controller } from "@hotwired/stimulus"
import { EditorView, basicSetup } from "codemirror"
import { html } from "@codemirror/lang-html"
import { json } from "@codemirror/lang-json"
import { javascript } from "@codemirror/lang-javascript"
import { markdown } from "@codemirror/lang-markdown"
import { oneDark } from "@codemirror/theme-one-dark"
import { EditorState } from "@codemirror/state"
import { linter, lintGutter } from "@codemirror/lint"

// walidator JSON - sprawdza składnię i pokazuje błędy
const jsonLinter = linter((view) => {
  const diagnostics = []
  const content = view.state.doc.toString()

  if (content.trim() === "") {
    return diagnostics // pusty JSON jest OK
  }

  try {
    JSON.parse(content)
  } catch (e) {
    // parsuj pozycję błędu z komunikatu
    const match = e.message.match(/position\s+(\d+)/i)
    let pos = match ? parseInt(match[1], 10) : 0
    pos = Math.min(pos, content.length)

    diagnostics.push({
      from: pos,
      to: Math.min(pos + 1, content.length),
      severity: "error",
      message: e.message,
    })
  }

  return diagnostics
})

export default class extends Controller {
  static targets = ["editor", "textarea", "content", "error"]
  static values = { lang: { type: String, default: "html" }, height: { type: String, default: "400px" }, theme: { type: String, default: "dark" } }

  editor = null
  isFullscreen = false

  connect() {
    if (this.hasEditorTarget && this.hasTextareaTarget) {
      this.createEditor()
    } else if (this.hasEditorTarget && this.hasContentTarget) {
      this.createReadonlyEditor()
    }

    // ESC key to exit fullscreen
    this.handleKeyDown = this.handleKeyDown.bind(this)
    document.addEventListener("keydown", this.handleKeyDown)
  }

  getLangExtension() {
    switch (this.langValue) {
      case "json":
        return [json(), lintGutter(), jsonLinter]
      case "javascript":
      case "js":
        return javascript()
      case "jsx":
      case "react":
        return javascript({ jsx: true })
      case "markdown":
        return markdown()
      case "svelte":
      case "vue":
        // Svelte i Vue to HTML z osadzonym JS - używamy HTML
        return html()
      default:
        return html()
    }
  }

  createEditor() {
    const textarea = this.textareaTarget
    const initialContent = textarea.value || ""

    // ukryj textarea
    textarea.style.display = "none"

    // stwórz przycisk fullscreen
    this.createFullscreenButton()

    // stwórz element na komunikaty o błędach (dla JSON)
    if (this.langValue === "json") {
      this.errorElement = document.createElement("div")
      this.errorElement.className = "hidden mt-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm"
      this.editorTarget.parentNode.insertBefore(this.errorElement, this.editorTarget.nextSibling)
    }

    // stwórz edytor CodeMirror
    this.editor = new EditorView({
      state: EditorState.create({
        doc: initialContent,
        extensions: [
          basicSetup,
          this.getLangExtension(),
          ...(this.themeValue === "dark" ? [oneDark] : []),
          EditorView.lineWrapping,
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              // synchronizuj z textarea i wyślij event dla innych kontrolerów
              textarea.value = update.state.doc.toString()
              textarea.dispatchEvent(new Event("input", { bubbles: true }))
              // waliduj JSON
              if (this.langValue === "json") {
                this.validateJson(update.state.doc.toString())
              }
              // aktualizuj widoczność przycisku fullscreen
              this.updateFullscreenButtonVisibility()
            }
          }),
          EditorView.theme({
            "&": {
              height: this.heightValue,
              border: this.themeValue === "dark" ? "1px solid #374151" : "1px solid #d1d5db",
              borderRadius: "0.375rem",
            },
            ".cm-scroller": {
              overflow: "auto",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: "14px",
            },
            ".cm-content": {
              minHeight: `${parseInt(this.heightValue) - 20}px`,
            },
          }),
        ],
      }),
      parent: this.editorTarget,
    })

    // udostępnij instancję EditorView na elemencie DOM (dla innych kontrolerów)
    this.element.cmView = this.editor

    // autofocus
    if (textarea.dataset.autofocus === "1") {
      setTimeout(() => {
        this.editor.focus()
      }, 100)
    }

    // sprawdź widoczność przycisku fullscreen
    this.updateFullscreenButtonVisibility()

    // waliduj początkową wartość
    if (this.langValue === "json") {
      this.validateJson(initialContent)
      // blokuj submit formularza przy błędnym JSON
      const form = textarea.closest("form")
      if (form) {
        form.addEventListener("submit", (e) => {
          if (!this.validateJson(this.editor.state.doc.toString())) {
            e.preventDefault()
            this.errorElement.scrollIntoView({ behavior: "smooth", block: "center" })
          }
        })
      }
    }
  }

  validateJson(content) {
    if (!this.errorElement) return true

    if (content.trim() === "") {
      this.errorElement.classList.add("hidden")
      return true
    }

    try {
      JSON.parse(content)
      this.errorElement.classList.add("hidden")
      return true
    } catch (e) {
      this.errorElement.textContent = `❌ Błąd JSON: ${e.message}`
      this.errorElement.classList.remove("hidden")
      return false
    }
  }

  createReadonlyEditor() {
    const content = this.contentTarget.textContent || ""

    // ukryj oryginalny content
    this.contentTarget.style.display = "none"

    this.editor = new EditorView({
      state: EditorState.create({
        doc: content,
        extensions: [
          basicSetup,
          this.getLangExtension(),
          EditorView.lineWrapping,
          EditorState.readOnly.of(true),
          EditorView.editable.of(false),
          EditorView.theme({
            "&": {
              maxHeight: "500px",
              border: "1px solid #e5e7eb",
              borderRadius: "0.375rem",
              backgroundColor: "#ffffff",
            },
            ".cm-scroller": {
              overflow: "auto",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: "14px",
            },
            ".cm-gutters": {
              backgroundColor: "#f9fafb",
              borderRight: "1px solid #e5e7eb",
            },
          }),
        ],
      }),
      parent: this.editorTarget,
    })
  }

  createFullscreenButton() {
    this.fullscreenBtn = document.createElement("button")
    this.fullscreenBtn.type = "button"
    this.fullscreenBtn.className = "mb-1 px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
    this.fullscreenBtn.textContent = "⛶ Fullscreen"
    this.fullscreenBtn.style.display = "none" // domyślnie ukryty
    this.fullscreenBtn.addEventListener("click", () => this.toggleFullscreen())
    this.editorTarget.parentNode.insertBefore(this.fullscreenBtn, this.editorTarget)
  }

  updateFullscreenButtonVisibility() {
    if (!this.fullscreenBtn || !this.editor) return

    const content = this.editor.state.doc.toString()
    const lineCount = content.split("\n").length

    // pokaż przycisk tylko gdy > 20 linii
    if (lineCount > 20) {
      this.fullscreenBtn.style.display = "inline-block"
    } else {
      this.fullscreenBtn.style.display = "none"
    }
  }

  toggleFullscreen() {
    if (!this.isFullscreen) {
      this.enterFullscreen()
    } else {
      this.exitFullscreen()
    }
  }

  enterFullscreen() {
    this.isFullscreen = true

    // zapisz referencję do elementu (nie używaj this.editorTarget po przeniesieniu)
    this.editorElement = this.editorTarget

    // zapisz oryginalny parent i placeholder
    this.placeholder = document.createElement("div")
    this.placeholder.style.display = "none"
    this.editorElement.parentNode.insertBefore(this.placeholder, this.editorElement)

    // stwórz overlay
    this.overlay = document.createElement("div")
    this.overlay.style.cssText = "position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 9999; background: #1e1e1e; display: flex; flex-direction: column;"

    // toolbar w fullscreen
    const toolbar = document.createElement("div")
    toolbar.style.cssText = "display: flex; justify-content: space-between; align-items: center; padding: 8px 16px; background: #374151; border-bottom: 1px solid #4b5563;"

    const label = document.createElement("span")
    label.style.cssText = "color: #d1d5db; font-size: 14px;"
    label.textContent = "Edytor (ESC aby zamknąć)"

    const closeBtn = document.createElement("button")
    closeBtn.type = "button"
    closeBtn.style.cssText = "padding: 6px 12px; font-size: 14px; background: #2563eb; color: white; border: none; border-radius: 4px; cursor: pointer;"
    closeBtn.textContent = "✕ Zamknij"
    closeBtn.onclick = () => this.exitFullscreen()

    toolbar.appendChild(label)
    toolbar.appendChild(closeBtn)

    // kontener na edytor
    const editorContainer = document.createElement("div")
    editorContainer.style.cssText = "flex: 1; overflow: auto; display: flex; flex-direction: column;"

    this.overlay.appendChild(toolbar)
    this.overlay.appendChild(editorContainer)
    document.body.appendChild(this.overlay)

    // przenieś edytor
    editorContainer.appendChild(this.editorElement)

    // zmień wysokość edytora
    const cmElement = this.editorElement.querySelector(".cm-editor")
    if (cmElement) {
      this.originalHeight = cmElement.style.height
      cmElement.style.height = "100%"
      cmElement.style.maxHeight = "none"
    }
    // ustaw też kontener edytora
    this.editorElement.style.height = "100%"
    this.editorElement.style.flex = "1"

    this.fullscreenBtn.textContent = "⛶ Exit Fullscreen"
  }

  exitFullscreen() {
    if (!this.isFullscreen) return
    this.isFullscreen = false

    // przywróć edytor do oryginalnego miejsca (przed placeholder)
    if (this.placeholder && this.placeholder.parentNode && this.editorElement) {
      this.placeholder.parentNode.insertBefore(this.editorElement, this.placeholder)
      this.placeholder.remove()
      this.placeholder = null
    }

    // przywróć wysokość
    if (this.editorElement) {
      const cmElement = this.editorElement.querySelector(".cm-editor")
      if (cmElement) {
        cmElement.style.height = this.originalHeight || this.heightValue
        cmElement.style.maxHeight = ""
      }
      this.editorElement.style.height = ""
      this.editorElement.style.flex = ""
    }

    // usuń overlay
    if (this.overlay) {
      this.overlay.remove()
      this.overlay = null
    }

    this.fullscreenBtn.textContent = "⛶ Fullscreen"
    this.editorElement = null
  }

  handleKeyDown(event) {
    if (event.key === "Escape" && this.isFullscreen) {
      event.preventDefault()
      this.exitFullscreen()
    }
  }

  // wstawia snippet w miejscu kursora
  insertSnippet(event) {
    this.insertText(event.currentTarget.dataset.snippet || "")
  }

  // wstawia snippet dociągany z serwera (data-snippet-url -> JSON {content}) -
  // duże szablony (np. systemowe layouty stron) nie mieszczą się sensownie w atrybutach
  async insertRemoteSnippet(event) {
    const url = event.currentTarget.dataset.snippetUrl
    if (!url) return

    try {
      const resp = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      const data = await resp.json()
      this.insertText(data.content || "")
      this.syncField(event.currentTarget)
    } catch {
      // brak wstawienia - użytkownik może kliknąć ponownie
    }
  }

  // opcjonalna synchronizacja pola formularza z wstawionym snippetem
  // (data-sync-field-id + data-sync-field-value) - np. select system_template layoutu
  // ma pokazywać szablon, którego markup właśnie wstawiono
  syncField(button) {
    const fieldId = button.dataset.syncFieldId
    if (!fieldId) return

    const field = document.getElementById(fieldId)
    if (field) field.value = button.dataset.syncFieldValue || ""
  }

  insertText(snippet) {
    if (!this.editor || !snippet) return

    const state = this.editor.state
    const selection = state.selection.main

    this.editor.dispatch({
      changes: { from: selection.from, to: selection.to, insert: snippet },
      selection: { anchor: selection.from + snippet.length }
    })
    this.editor.focus()
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeyDown)
    if (this.isFullscreen) {
      this.exitFullscreen()
    }
    if (this.editor) {
      this.editor.destroy()
    }
  }
}
